import {
  ApiService,
  type Company,
  type CreateCompanyRequest,
  type UpdateCompanyRequest,
  type CompaniesPaginatedResponse,
  type CompaniesQuery,
} from './api.service'

export class CompanyService extends ApiService {
  /**
   * Get all companies with pagination
   */
  async getCompanies(query?: CompaniesQuery): Promise<CompaniesPaginatedResponse> {
    return this.handleResponse(
      this.api.get<CompaniesPaginatedResponse>('/companies', {
        params: query,
      })
    )
  }

  /**
   * Get a company by ID
   */
  async getCompanyById(id: string): Promise<Company> {
    return this.handleResponse(this.api.get<Company>(`/companies/${id}`))
  }

  /**
   * Create a new company
   */
  async createCompany(companyData: CreateCompanyRequest): Promise<Company> {
    return this.handleResponse(this.api.post<Company>('/companies', companyData))
  }

  /**
   * Update a company
   */
  async updateCompany(id: string, companyData: UpdateCompanyRequest): Promise<Company> {
    return this.handleResponse(this.api.patch<Company>(`/companies/${id}`, companyData))
  }

  /**
   * Delete a company (soft delete)
   */
  async deleteCompany(id: string): Promise<void> {
    return this.handleResponse(this.api.delete<void>(`/companies/${id}`))
  }

  /**
   * Upload a company logo image
   */
  async uploadLogo(file: File): Promise<{ logoUrl: string }> {
    const formData = new FormData()
    formData.append('logo', file)

    return this.handleResponse(
      this.api.post<{ logoUrl: string }>('/companies/upload-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    )
  }

  /**
   * Public self-signup: register a new company + initial admin user
   */
  async signupCompany(data: {
    companyName: string
    companyDescription?: string
    contactPersonName?: string
    contactPersonEmail: string
    contactPersonPhone?: string
    adminFirstName: string
    adminLastName: string
    adminEmail: string
    adminPassword: string
  }): Promise<{ companyId: string; adminUserId: string; status: string }> {
    return this.handleResponse(
      this.api.post<{ companyId: string; adminUserId: string; status: string }>(
        '/public/companies/signup',
        data,
        // Public signup does not need the session cookie. Omitting credentials lets the
        // browser read error JSON when the API answers with Access-Control-Allow-Origin: *.
        { withCredentials: false }
      )
    )
  }

  /**
   * Superadmin: approve/unapprove a company (optionally set limits)
   */
  async updateApproval(
    id: string,
    data: {
      isApproved: boolean
      maxLicenses?: number
    }
  ): Promise<Company> {
    return this.handleResponse(this.api.patch<Company>(`/companies/${id}/approval`, data))
  }

  /**
   * Superadmin: update enabled modules for a company
   */
  async updateModules(id: string, enabledModules: string[]): Promise<Company> {
    return this.handleResponse(
      this.api.patch<Company>(`/companies/${id}/modules`, { enabledModules })
    )
  }
}

// Export singleton instance
export const companyService = new CompanyService()
